// Article « Budget mariage » (référencement). En français : il vise les
// recherches Google françaises. La répartition par poste vient de la répartition
// automatique de l'app (components/admin/BudgetPanel) : article et outil
// proposent les mêmes parts.

export const UPDATED = '2026-10-15';
export const READING_MINUTES = 10;

// Repères de marché (Rapport sur le secteur nuptial 2026 de Mariages.net,
// mariages célébrés en 2025, hors bague et voyage de noces)
export const AVERAGE_BUDGET = 19293;
export const AVERAGE_GUESTS = 90;
export const PER_GUEST = 215;

export const TOC = [
  { id: 'budget-moyen', label: 'Le budget moyen d’un mariage en France' },
  { id: 'nombre-invites', label: 'Budget mariage selon le nombre d’invités' },
  { id: 'repartition', label: 'La répartition du budget, poste par poste' },
  { id: 'etablir', label: 'Établir son budget en 5 étapes' },
  { id: 'suivre', label: 'Suivre ses dépenses : acomptes, échéances, soldes' },
  { id: 'economies', label: '10 astuces pour réduire le budget' },
  { id: 'oublis', label: 'Les dépenses qu’on oublie' },
  { id: 'faq', label: 'Questions fréquentes' },
];

// Budget indicatif selon le nombre d'invités (coût moyen par invité)
export const GUEST_COUNTS = [50, 80, 100, 120, 150];

export const STEPS = [
  {
    title: 'Fixez ce que vous pouvez mettre',
    text: 'Vos économies, ce que vous pouvez épargner d’ici le mariage, et l’éventuelle participation des familles. Ce total, et pas une moyenne, est votre budget.',
  },
  {
    title: 'Décidez du nombre d’invités',
    text: 'C’est le premier levier : traiteur, boissons, location de vaisselle et une partie du lieu se paient au couvert.',
  },
  {
    title: 'Choisissez vos trois priorités',
    text: 'La nourriture, les photos, la musique, le lieu… Mettez plus sur ce qui compte pour vous, et allégez le reste.',
  },
  {
    title: 'Répartissez poste par poste',
    text: 'Partez des parts habituelles ci-dessus, ajustées à vos priorités et à votre nombre d’invités, puis arrondissez.',
  },
  {
    title: 'Gardez une marge pour les imprévus',
    text: 'Autour de 5 % du total : heures supplémentaires, invités en plus, retouches de dernière minute.',
  },
];

export const SAVINGS = [
  {
    title: 'Choisissez un vendredi ou une date hors saison',
    text: 'Les lieux et les prestataires sont souvent moins chers hors de mai à septembre et en dehors du samedi.',
  },
  { title: 'Réduisez la liste d’invités', text: 'Chaque invité en moins, c’est en moyenne plus de 200 € économisés.' },
  {
    title: 'Allégez le vin d’honneur',
    text: 'Un cocktail plus court, ou des pièces salées en moins, pèse vite sur la facture du traiteur.',
  },
  {
    title: 'Comparez au moins trois devis',
    text: 'Pour le lieu, le traiteur et le photographe, les écarts de prix sont importants à prestation égale.',
  },
  {
    title: 'Demandez un lieu avec traiteur attitré',
    text: 'Un forfait lieu et repas évite des frais de location de cuisine et de vaisselle.',
  },
  {
    title: 'Fournissez les boissons',
    text: 'Quand le traiteur l’accepte, apporter le vin coûte souvent moins cher, même avec un droit de bouchon.',
  },
  {
    title: 'Faites une partie de la déco vous-mêmes',
    text: 'Centres de table, signalétique, livre d’or : à préparer avec vos proches les mois d’avant.',
  },
  {
    title: 'Invitez en ligne',
    text: 'Un site d’invitation avec réponse en ligne remplace une partie de la papeterie et les relances.',
  },
  {
    title: 'Louez plutôt qu’acheter',
    text: 'Tenue, accessoires, décoration : la location et la seconde main sont devenues courantes.',
  },
  {
    title: 'Une playlist pour le vin d’honneur',
    text: 'Gardez le DJ ou le groupe pour la soirée, et une enceinte suffit pour l’apéritif.',
  },
];

export const FORGOTTEN = [
  {
    title: 'Les heures supplémentaires',
    text: 'Photographe, DJ, personnel de service : au-delà de l’horaire prévu, l’heure se paie.',
  },
  {
    title: 'Le droit de bouchon',
    text: 'Si vous apportez vos boissons, le traiteur ou le lieu facture souvent le service par bouteille.',
  },
  {
    title: 'La location de vaisselle et de linge',
    text: 'Pas toujours incluse chez le traiteur : nappes, verres, couverts, mobilier.',
  },
  {
    title: 'Le ménage et la caution du lieu',
    text: 'La caution n’est pas une dépense, mais elle doit être disponible ; le ménage, si.',
  },
  { title: 'Les retouches et les accessoires', text: 'Retouches de la robe ou du costume, chaussures, voile, bijoux.' },
  {
    title: 'L’hébergement et les repas des prestataires',
    text: 'Repas du photographe et du DJ, parfois leur nuit sur place.',
  },
  { title: 'Le brunch du lendemain', text: 'Souvent ajouté en cours de route, il représente un deuxième repas.' },
];

export const FAQ = [
  {
    q: 'Quel est le budget moyen d’un mariage en France ?',
    a: 'Environ 19 300 € en 2025 pour 90 invités, hors bague et voyage de noces, selon le Rapport sur le secteur nuptial 2026 de Mariages.net, soit autour de 215 € par invité. Plus de 40 % des couples dépensent 20 000 € ou plus.',
  },
  {
    q: 'Quel budget pour un mariage de 100 personnes ?',
    a: 'Autour de 21 000 à 22 000 € en moyenne, en partant d’environ 215 € par invité. C’est un ordre de grandeur : la région, la saison et vos priorités le font varier fortement.',
  },
  {
    q: 'Quelle part du budget pour le traiteur ?',
    a: 'C’est le premier poste : environ 30 % du budget, et un peu plus d’un tiers avec les boissons. Pour un dîner assis, comptez souvent 80 à 150 € par personne, hors boissons.',
  },
  {
    q: 'Qui paie le mariage ?',
    a: 'Aujourd’hui, le plus souvent les mariés eux-mêmes, sur leurs économies, avec une participation des familles pour environ un tiers des couples. Il n’y a plus de règle : l’important est de fixer le montant de chacun dès le départ.',
  },
  {
    q: 'Faut-il compter la bague et le voyage de noces dans le budget ?',
    a: 'Les moyennes publiées les excluent généralement. Si vous les financez avec la même enveloppe, ajoutez-les comme postes à part entière pour ne pas être surpris.',
  },
  {
    q: 'Tableau Excel ou application pour le budget ?',
    a: 'Un tableur demande de tout saisir et recalculer à la main. Une application répartit le budget pour vous, suit les acomptes et les soldes, et vous prévient avant chaque échéance.',
  },
];
